import {
  bfMetricDataConverterV3,
  BfMetricTileType,
  bfMetricUnitTableHeaderDisplayNameV3,
  IBfMetricListItem,
  IBfMetricsDisplayData,
  UnitSystem,
} from '@trackman/baseball-shared-components';
import SimpleTable from 'components/table/SimpleTable';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import { ListViewWrapper } from './styles/ListViewStyles';

interface IProps {
  data: IBfMetricsDisplayData[];
  metrics: IBfMetricListItem[];
  unitSystem: UnitSystem;
  itemCounter: number;
}

export const ListView: React.FC<IProps> = observer(({ data, metrics, unitSystem, itemCounter }) => {
  const stickyColumnRef = useRef(null);
  const mainTableRef = useRef(null);
  const isSyncingRef = useRef(false);

  const getColumns = React.useMemo(() => {
    return [
      ...metrics
        .filter(
          (m) =>
            m.type !== BfMetricTileType.strikeZone &&
            m.type !== BfMetricTileType.strikeZone3d &&
            m.type !== BfMetricTileType.strikeZoneDecision &&
            m.type !== BfMetricTileType.spin3d &&
            m.isEnabled
        )
        .map((m) => ({
          Header: m.name,
          SubHeader: bfMetricUnitTableHeaderDisplayNameV3(unitSystem, m.type),
          accessor: m.type,
          minWidth: 144,
          width: 144,
          Cell: (cell: any, accessor: string) => {
            return <div>{bfMetricDataConverterV3(unitSystem, m.type, cell[accessor])}</div>;
          },
        })),
    ];
  }, [data, itemCounter, metrics]);

  useEffect(() => {
    if (!stickyColumnRef.current || !mainTableRef.current) return;
    const syncScroll = (source, target) => {
      return () => {
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        target.scrollTop = source.scrollTop;
        target.scrollLeft = source.scrollLeft;
        requestAnimationFrame(() => {
          isSyncingRef.current = false;
        });
      };
    };

    const handleScrollA = syncScroll(stickyColumnRef.current, mainTableRef.current);
    const handleScrollB = syncScroll(mainTableRef.current, stickyColumnRef.current);
    if (stickyColumnRef.current && mainTableRef.current) {
      stickyColumnRef.current.addEventListener('scroll', handleScrollA);
      mainTableRef.current.addEventListener('scroll', handleScrollB);
    }

    return () => {
      if (stickyColumnRef.current && mainTableRef.current) {
        stickyColumnRef.current.removeEventListener('scroll', handleScrollA);
        mainTableRef.current.removeEventListener('scroll', handleScrollB);
      }
    };
  }, []);

  return (
    <ListViewWrapper className='row'>
      <SimpleTable
        className='sticky-column'
        columns={[
          {
            Header: 'Pitch',
            accessor: 'index',
            Cell: (cell: any, accessor: string) => {
              return <div>{`${parseFloat(cell[accessor])}`}</div>;
            },
          },
        ]}
        data={data}
        withFooter={false}
        width={'80px'}
        maxHeight='calc(100vh - 332px'
        innerRef={stickyColumnRef}
        variant='sticky'
        stickyHeader={true}
      />
      <SimpleTable
        innerRef={mainTableRef}
        className='main-table'
        columns={getColumns}
        data={data}
        withFooter={false}
        width={'calc(100% - 80px)'}
        maxHeight='calc(100vh - 332px'
        stickyHeader={true}
      />
    </ListViewWrapper>
  );
});
